import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Form, Input, InputNumber, Layout, Radio, Result, Select, Switch, message } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { Navigate, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import MainHeader from '@/components/mainheader';
import MainFooter from '@/components/mainfooter';
import PageTopbar from '@/components/pagetopbar';
import PageBack from '@/components/pageback';
import usePageBack from '@/utils/usePageBack';
import { loadPostTags, publishPost } from '@/store/modules/posts';
import ImagePicker from '@/components/imagepicker';
import PostOptions, { postOptionsPayload } from '@/components/postoptions';
import ImageQuota from '@/components/imagequota';
import PostFormSection from '@/components/postform';
import { fetchPostImageQuota } from '@/apis/posts';
import { uploadSelectedImages } from '@/apis/uploads';
import useMobile from '@/utils/useMobile';
import SchoolNotice from '@/components/schoolnotice';
import './index.scss';

const { Content } = Layout;
const { TextArea } = Input;

const CreatePostHeading = ({ recruiting }) => (
  <div className="post-create-hero">
    <div className="post-create-heading">
      <h1>{recruiting ? '发布搭子招募' : '发布帖子'}</h1>
      <span className="post-create-eyebrow">{recruiting ? 'FIND YOUR PARTNER' : 'SHARE YOUR MOMENT'}</span>
    </div>
  </div>
);

const validateFutureTime = (_, value) => {
  const time = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(time) && time > Date.now()
    ? Promise.resolve()
    : Promise.reject(new Error('请选择晚于当前时间的截止时间'));
};

const CreatePost = () => {
  const navigate = useNavigate();
  const goBack = usePageBack('/');
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const isMobile = useMobile();
  const submittingRef = useRef(false);
  const [publishedPost, setPublishedPost] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [mapBusy, setMapBusy] = useState(false);
  const { isAuthenticated, userInfo } = useSelector(state => state.user);
  const { tags, tagsStatus, tagsError, publishStatus } = useSelector(state => state.posts);
  const expireType = Form.useWatch('expire_type', form);
  const isRecruiting = Form.useWatch('is_recruiting', form) !== false;

  useEffect(() => {
    if (isAuthenticated && tagsStatus === 'idle') dispatch(loadPostTags());
  }, [dispatch, isAuthenticated, tagsStatus]);

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!userInfo?.school) return <Layout className="post-create-page"><MainHeader />{isMobile && <PageTopbar className="post-create-mobile-topbar" title="发布帖子" onBack={goBack} />}<PageBack className="post-create-back" onBack={goBack} /><Content className="post-create-content"><div className="post-create-shell pageBack-shell"><SchoolNotice /></div></Content><MainFooter /></Layout>;

  const uploadImages = async (postId, files) => {
    setUploading(true);
    try {
      const failed = await uploadSelectedImages(postId, files);
      setImageFiles(failed);
      if (failed.length) message.warning('帖子已发布，部分图片上传失败，可在下方重试');
      else message.success('图片上传成功');
    } finally { setUploading(false); }
  };
  const onFinish = async values => {
    if (submittingRef.current || mapBusy) return;
    if (imageFiles.length > 9) return message.warning('每个帖子最多 9 张图片');
    submittingRef.current = true;

    try {
      const postData = {
        title: values.title.trim(),
        description: values.description.trim(),
        tag_ids: values.tag_ids || [],
        images: [],
        is_recruiting: values.is_recruiting !== false,
        expire_type: values.expire_type,
        ...(values.expire_type === 'specific'
          ? { expire_time: new Date(values.expire_time).toISOString() }
          : { expire_value: values.expire_value }),
        ...(values.is_recruiting !== false ? {
          location: values.location?.trim() || '',
          ...postOptionsPayload(values),
          max_participants: values.max_participants,
        } : {}),
      };
      const post = await dispatch(publishPost(postData)).unwrap();
      if (!post?.id) throw new Error('帖子发布响应无效');
      setPublishedPost({ ...post, is_recruiting: post.is_recruiting ?? postData.is_recruiting });
      form.resetFields();
      message.success(postData.is_recruiting ? '搭子招募发布成功' : '帖子发布成功');
      if (imageFiles.length) await uploadImages(post.id, imageFiles);
    } catch (error) {
      message.error(typeof error === 'string' ? error : '发布失败，请稍后重试');
    } finally {
      submittingRef.current = false;
    }
  };

  const now = new Date();
  const minimumDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString().slice(0, 16);

  return (
    <Layout className="post-create-page">
      <MainHeader />
      {isMobile && <PageTopbar className="post-create-mobile-topbar" title={isRecruiting ? '发布搭子招募' : '发布帖子'} disabled={publishStatus === 'loading' || uploading} backLabel="返回" onBack={goBack} />}
      {!publishedPost && <PageBack className="post-create-back" disabled={publishStatus === 'loading' || uploading} onBack={goBack} />}
      <Content className={`post-create-content${publishedPost ? ' post-create-content-success' : ''}`}>
        <div className="post-create-shell pageBack-shell">
          {publishedPost ? (
            <Card className="post-create-card post-create-success-card">
              <Result
                status="success"
                title={publishedPost.is_recruiting ? '搭子招募已发布' : '帖子已发布'}
                subTitle={uploading ? '帖子已发布，正在上传图片…' : imageFiles.length ? '帖子已发布，以下图片尚未上传成功，请重试。' : publishedPost.is_recruiting ? '你的需求已经提交，可以继续发布新的招募。' : '你的内容已经发布，可以继续发布新的帖子。'}
                extra={[
                  <Button key="again" type="primary" disabled={uploading || imageFiles.length > 0} onClick={() => setPublishedPost(null)}>继续发布</Button>,
                  <Button key="home" disabled={uploading} onClick={() => navigate('/')}>返回首页</Button>,
                ]}
              />
              {imageFiles.length > 0 && <div className="post-create-upload-retry">
                <ImagePicker files={imageFiles} onChange={setImageFiles} disabled={uploading} allowAdd={false} />
                <Button loading={uploading} disabled={uploading} onClick={() => uploadImages(publishedPost.id, imageFiles)}>重试上传</Button>
                <Button disabled={uploading} onClick={() => setImageFiles([])}>放弃剩余图片</Button>
              </div>}
            </Card>
          ) : (
            <>
            {!isMobile && <CreatePostHeading recruiting={isRecruiting} />}
            <Card className="post-create-card">
              {isMobile && <CreatePostHeading recruiting={isRecruiting} />}
                <Form
                  className="post-form"
                  form={form}
                  disabled={publishStatus === 'loading' || uploading}
                  layout="vertical"
                  requiredMark
                  scrollToFirstError
                  initialValues={{ post_type: 'general', location_visibility: 'approximate', expire_type: 'duration', expire_value: 24, max_participants: 1, tag_ids: [], is_recruiting: true }}
                  onFinish={onFinish}
                >
                  <PostFormSection number="01" title={isRecruiting ? '招募内容' : '帖子内容'} description={isRecruiting ? '让大家一眼看懂你想找什么样的搭子' : '分享你想说的事'}>
                    <Form.Item
                      name="title"
                      label="标题"
                      rules={[{ required: true, whitespace: true, message: '请填写标题' }]}
                    >
                      <Input maxLength={100} showCount placeholder={isRecruiting ? '例如：周六下午一起打羽毛球' : '写一个标题'} />
                    </Form.Item>
                    <Form.Item
                      name="description"
                      label="详细描述"
                      rules={[{ required: true, whitespace: true, message: isRecruiting ? '请描述活动安排' : '请填写帖子内容' }]}
                    >
                      <TextArea autoSize={{ minRows: 5, maxRows: 12 }} maxLength={2000} showCount placeholder={isRecruiting ? '介绍时间、活动内容以及你希望搭子了解的细节' : '分享你想说的内容'} />
                    </Form.Item>
                    <Form.Item name="tag_ids" label="标签" extra={isRecruiting ? '选择与招募内容相关的标签，方便其他人找到你。' : '选择与帖子内容相关的标签。'}>
                      <Select
                        mode="multiple"
                        maxCount={5}
                        maxTagCount="responsive"
                        loading={tagsStatus === 'loading'}
                        optionFilterProp="label"
                        placeholder={tagsStatus === 'loading' ? '正在加载标签…' : '选择标签（可选）'}
                        options={tags.map(tag => ({ label: tag.name, value: tag.id }))}
                      />
                    </Form.Item>
                    {tagsStatus === 'failed' && (
                      <Alert
                        className="post-form-tag-alert"
                        type="warning"
                        showIcon
                        message={tagsError || '标签加载失败，可稍后重试或不选标签直接发布'}
                        action={<Button size="small" onClick={() => dispatch(loadPostTags())}>重试</Button>}
                      />
                    )}
                  </PostFormSection>

                  <PostFormSection number="02" title="活动安排" description={isRecruiting ? '地点与截止时间可以帮助伙伴决定是否加入' : '设置帖子的持续时长或指定截止时间'}>
                    <Form.Item name="is_recruiting" label="开放招募" valuePropName="checked" extra={isRecruiting ? '开放后，其他用户可以通过评论申请加入，审批通过后自动加入帖子群聊。' : '关闭后，帖子仅用于分享交流，不接受招募申请；仍可设置持续时长或指定截止时间。'}><Switch /></Form.Item>
                    <Form.Item name="expire_type" label={isRecruiting ? '招募有效期' : '帖子有效期'}>
                      <Radio.Group
                        optionType="button"
                        buttonStyle="solid"
                        options={[{ label: '持续时长', value: 'duration' }, { label: '指定截止时间', value: 'specific' }]}
                      />
                    </Form.Item>
                    {expireType === 'specific' ? (
                      <Form.Item name="expire_time" label="截止时间" required rules={[{ validator: validateFutureTime }]}>
                        <Input type="datetime-local" min={minimumDateTime} />
                      </Form.Item>
                    ) : (
                      <Form.Item
                        name="expire_value"
                        label="持续时长（小时）"
                        rules={[{ required: true, message: '请填写持续时长' }]}
                      >
                        <InputNumber min={1} max={720} precision={0} placeholder="1–720 小时" />
                      </Form.Item>
                    )}
                    {isRecruiting && <Form.Item
                      name="max_participants"
                      label="招募人数"
                      extra="填写你希望招募的伙伴人数。"
                      rules={[{ required: true, message: '请填写招募人数' }]}
                    >
                      <InputNumber min={1} max={99} precision={0} placeholder="至少 1 人" />
                    </Form.Item>}
                    {isRecruiting && <>
                      <PostOptions form={form} defaultCurrentLocation disabled={publishStatus === 'loading' || uploading} onBusyChange={setMapBusy} />
                    </>}
                  </PostFormSection>

                  <PostFormSection number="03" title="补充图片" description={isRecruiting ? '上传图片，让活动更直观' : '上传图片，让内容更直观'}>
                    <div className="post-form-image-fields">
                      <ImageQuota fetchQuota={fetchPostImageQuota} selectedFiles={imageFiles} showAccountQuota={false} />
                      <ImagePicker files={imageFiles} onChange={setImageFiles} maxCount={9} disabled={publishStatus === 'loading'} />
                    </div>
                  </PostFormSection>

                  <div className="post-form-actions">
                    <Button onClick={goBack}>取消</Button>
                    <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={publishStatus === 'loading'} disabled={mapBusy}>
                      {isRecruiting ? '发布招募' : '发布帖子'}
                    </Button>
                  </div>
                </Form>
            </Card>
            </>
          )}
        </div>
      </Content>
      <MainFooter />
    </Layout>
  );
};

export default CreatePost;
